import { FC, useLayoutEffect } from 'react';
import { REPORT_PREVIEW_PDF_MARGIN_MM } from 'app/modules/analytics/modules/reports/constants/reportPreviewPageShellClassName';

export const ANALYTICS_REPORT_PDF_STYLES_ID = 'analytics-report-pdf-styles';

const analyticsReportPdfPageMarginCss = `
  @page {
    size: auto;
    margin: ${REPORT_PREVIEW_PDF_MARGIN_MM}mm;
  }
`;

export const analyticsReportPdfPostMediaCss = `
  .analytics-report-pdf-export {
    width: 100%;
  }

  .analytics-report-pdf-export * {
    border-radius: 0 !important;
    box-shadow: none !important;
  }

  .analytics-report-pdf-export .tw-rounded-full {
    border-radius: 9999px !important;
  }

  .analytics-report-pdf-export article {
    box-sizing: border-box;
    max-width: 210mm;
    margin-left: auto;
    margin-right: auto;
    width: 100%;
    background: #ffffff !important;
  }

  .analytics-report-pdf-export .analytics-report-section-block {
    background: none !important;
    padding: 0 !important;
  }

  .analytics-report-pdf-export .analytics-report-section-block > div:first-child {
    padding-left: 0 !important;
    padding-right: 0 !important;
  }

  .analytics-report-pdf-export .tw-border-grayscale-10 {
    border-color: #d7dbe1 !important;
  }

  .analytics-report-pdf-export .analytics-report-section-note {
    border: 1px solid #d7dbe1 !important;
  }

  .analytics-report-pdf-export .analytics-report-metric-cards-row {
    display: grid !important;
    align-items: stretch !important;
    gap: 4px !important;
  }

  .analytics-report-pdf-export .analytics-report-metric-cards-row > * {
    box-sizing: border-box !important;
    min-width: 0 !important;
  }

  .analytics-report-pdf-export
    .analytics-report-metric-cards-row.tw-grid-cols-1 {
    grid-template-columns: repeat(1, minmax(0, 1fr)) !important;
  }

  .analytics-report-pdf-export
    .analytics-report-metric-cards-row.tw-grid-cols-2 {
    grid-template-columns: repeat(2, minmax(0, 1fr)) !important;
  }

  .analytics-report-pdf-export
    .analytics-report-metric-cards-row.tw-grid-cols-2
    > *:last-child:nth-child(odd) {
    grid-column: 1 / -1 !important;
  }

  .analytics-report-pdf-export
    .analytics-report-metric-cards-row.tw-grid-cols-3 {
    grid-template-columns: repeat(3, minmax(0, 1fr)) !important;
  }

  .analytics-report-pdf-export
    .analytics-report-metric-cards-row.tw-grid-cols-4 {
    grid-template-columns: repeat(4, minmax(0, 1fr)) !important;
  }

  .analytics-report-pdf-export
    .analytics-report-metric-cards-row.tw-grid-cols-5 {
    grid-template-columns: repeat(5, minmax(0, 1fr)) !important;
  }

  .analytics-report-pdf-export .analytics-report-section-block,
  .analytics-report-pdf-export .analytics-report-metric-cards-row,
  .analytics-report-pdf-export .analytics-report-metric-cards-row > * {
    break-inside: avoid !important;
    page-break-inside: avoid !important;
  }

  .analytics-report-pdf-export .analytics-report-page-header-name {
    display: -webkit-box !important;
    -webkit-box-orient: vertical !important;
    -webkit-line-clamp: 2 !important;
    overflow: hidden !important;
  }

  .analytics-report-pdf-export .analytics-report-top-post-card-media,
  .analytics-report-pdf-export
    .analytics-report-top-posts-grid
    > div
    > .analytics-report-top-post-card-media {
    aspect-ratio: unset !important;
    max-height: none !important;
    min-height: 139px !important;
    height: 139px !important;
    width: 100% !important;
    max-width: 100% !important;
    overflow: hidden !important;
  }

  .analytics-report-pdf-export .analytics-report-top-post-card-media img,
  .analytics-report-pdf-export
    .analytics-report-top-posts-grid
    > div
    > .analytics-report-top-post-card-media
    img {
    display: block !important;
    width: 100% !important;
    height: 100% !important;
    min-width: 100% !important;
    max-width: none !important;
    object-fit: cover !important;
  }

  .analytics-report-pdf-export
    .analytics-report-top-post-card-media
    > div:not(.tw-absolute) {
    width: 100% !important;
    height: 100% !important;
  }

  .analytics-report-pdf-export .analytics-report-overview-chart-grid {
    display: grid !important;
    grid-template-columns: repeat(2, minmax(0, 1fr)) !important;
    gap: 16px !important;
    break-inside: avoid !important;
    page-break-inside: avoid !important;
  }

  .analytics-report-pdf-export .analytics-report-overview-kpi-row {
    display: flex !important;
    flex-wrap: wrap !important;
    gap: 8px !important;
  }

  .analytics-report-pdf-export .analytics-report-overview-kpi-row > * {
    flex: 1 1 calc(25% - 8px) !important;
    min-width: 120px !important;
  }

  .analytics-report-pdf-export .analytics-report-overview-posts-grid {
    display: grid !important;
    gap: 12px !important;
    break-inside: avoid !important;
    page-break-inside: avoid !important;
  }

  .analytics-report-pdf-export
    .analytics-report-overview-posts-grid.tw-grid-cols-3 {
    grid-template-columns: repeat(3, minmax(0, 1fr)) !important;
  }

  .analytics-report-pdf-export
    .analytics-report-overview-posts-grid.tw-grid-cols-4 {
    grid-template-columns: repeat(4, minmax(0, 1fr)) !important;
  }
`;

const AnalyticsReportPdfStyles: FC = () => {
  useLayoutEffect(() => {
    const css = `
      ${analyticsReportPdfPageMarginCss}
      ${analyticsReportPdfPostMediaCss}
      @media print {
        ${analyticsReportPdfPageMarginCss}
        ${analyticsReportPdfPostMediaCss}
      }
    `;

    let styleElement = document.getElementById(
      ANALYTICS_REPORT_PDF_STYLES_ID,
    ) as HTMLStyleElement | null;

    if (!styleElement) {
      styleElement = document.createElement('style');
      styleElement.id = ANALYTICS_REPORT_PDF_STYLES_ID;
      document.head.appendChild(styleElement);
    }

    styleElement.textContent = css;

    return () => {
      styleElement?.remove();
    };
  }, []);

  return null;
};

export default AnalyticsReportPdfStyles;
